import {
  RuleEngineService,
  convertNamedRuleExpressionToNumbers,
  convertNumberedRuleExpressionToNames,
  defaultRuleDisplayLabel,
  extractExpressionRuleTokens,
} from '../../services/rule-engine.service';

interface SatExprAstNode {
  type: 'num' | 'ref' | 'and' | 'or';
  value?: string;
  children?: SatExprAstNode[];
}

/** Expression builder prep for routing-ruleconfig1 (independent of legacy routing-ruleconfig). */
export class RoutingExpressionPrep {
  private satExprTok: string[] = [];
  private satExprTi = 0;

  prepareForBlock(
    block: { rules: unknown[]; ruleSatisfaction: string },
    ruleEngine: RuleEngineService
  ): void {
  
    const rules = (block.rules || []).map((r: any, idx: number) => ({
      rulenumber: r.rulenumber ?? idx + 1,
      rulename:
        (r.rulename || '').trim() ||
        defaultRuleDisplayLabel(Number(r.rulenumber ?? idx + 1)),
      field: r.field ?? '',
      value: r.value ?? '',
      operator: r.operator ?? r.operation,
      ruletype: r.ruletype ?? '',
      param: r.param ?? '',
    }));
    ruleEngine.setAllTheRules(rules);

    const sat = (block.ruleSatisfaction || '').trim();
    let expressionGroupObj: any;
    if (!sat) {
      expressionGroupObj = this.buildDefaultExpressionFromRules(rules);
    } else {
      expressionGroupObj = this.parseRuleSatisfactionToExpressionGroups(sat, rules);
      const groupedPrefixEndIndex = this.inferGroupedPrefixEndIndexFromExpression(sat);
      if (groupedPrefixEndIndex !== null) {
        const expanded = this.expandGroupedPrefixForVisualBuilder(sat, rules);
        if (expanded?.expressionGroupArray?.length) {
          expressionGroupObj = {
            ...expanded,
            groupedPrefixEndIndex: expanded.groupedPrefixEndIndex,
          };
        }
      }
    }
    ruleEngine.holdTheBuildExpressionData = expressionGroupObj;
    const namedSat = sat
      ? this.resolveFinalExpressionForBlock({ rules, ruleSatisfaction: sat })
      : '';
    if (namedSat || sat) {
      ruleEngine.setFinalRuleExpression(namedSat || sat);
    } else {
      const seeded = this.buildFinalExpressionFromBuilderData(expressionGroupObj);
      if (seeded) {
        ruleEngine.setFinalRuleExpression(seeded);
      }
    }
  }

  private buildDefaultExpressionFromRules(rules: any[]): any {
    const expressionArray = rules.map((r: any, i: number) => ({
      condition: i === 0 ? '' : 'AND',
      rule:
        (r.rulename || '').trim() ||
        defaultRuleDisplayLabel(Number(r.rulenumber ?? i + 1)),
    }));
    return {
      expressionGroupArray: [{ expressionArray, groupOperator: '' }],
    };
  }

  /**
   * Restore expression builder groups from saved text (e.g. "1 or (2 and 3)" or "(A OR (B AND C))").
   * Uses parentheses + AND/OR precedence; falls back to legacy flat split if parse fails.
   */
  private parseRuleSatisfactionToExpressionGroups(ruleSatisfaction: string, rules: any[]): any {
    const normalized = (ruleSatisfaction || '')
      .toString()
      .replace(/\bAND\b/gi, 'and')
      .replace(/\bOR\b/gi, 'or')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalized) {
      return this.buildDefaultExpressionFromRules(rules);
    }

    /**
     * Prefer splitting at parens depth 0 — matches how the builder serializes
     * `(group1 inner) OR (group2 inner)`. Flattening OR chains in the AST
     * would wrongly merge `(A OR B) OR (C)` into one group.
     */
    const top = this.splitTopLevelSegments(normalized);
    if (top.segments.length > 1) {
      const expressionGroupArray: any[] = [];
      for (let idx = 0; idx < top.segments.length; idx++) {
        const seg = (top.segments[idx] || '').trim();
        if (!seg) {
          continue;
        }
        const ast = this.parseSatExpressionAst(seg, rules);
        if (!ast) {
          return this.parseRuleSatisfactionToExpressionLegacyFlatten(ruleSatisfaction, rules);
        }
        const rows = this.satAstToRows(ast, rules).map((r) => ({
          condition: r.condition,
          rule: r.rule,
        }));
        if (!rows.length) {
          continue;
        }
        expressionGroupArray.push({
          expressionArray: rows,
          groupOperator:
            idx < top.operators.length ? top.operators[idx] : '',
        });
      }
      if (expressionGroupArray.length) {
        return { expressionGroupArray };
      }
    }

    const single =
      top.segments.length === 1 ? top.segments[0].trim() : normalized;
    const ast = this.parseSatExpressionAst(single, rules);
    if (ast) {
      const fromAst = this.satAstToExpressionGroupObj(ast, rules);
      if (fromAst?.expressionGroupArray?.length) {
        return fromAst;
      }
    }

    return this.parseRuleSatisfactionToExpressionLegacyFlatten(ruleSatisfaction, rules);
  }

  /**
   * Split expression at depth 0 on `and` / `or` so serialized builder output
   * `(a OR b) OR (c)` becomes three chunks (ops between chunks match Group Condition controls).
   */
  private splitTopLevelSegments(expr: string): {
    segments: string[];
    operators: ('AND' | 'OR')[];
  } {
    const segments: string[] = [];
    const operators: ('AND' | 'OR')[] = [];
    let depth = 0;
    let start = 0;
    let i = 0;

    while (i < expr.length) {
      const c = expr[i];
      if (c === '(') {
        depth++;
        i++;
        continue;
      }
      if (c === ')') {
        depth--;
        i++;
        continue;
      }
      if (depth === 0) {
        const rest = expr.slice(i);
        const opMatch = rest.match(/^\s*\b(or|and)\b/i);
        if (opMatch) {
          const word = opMatch[1].toLowerCase();
          const op: 'AND' | 'OR' = word === 'or' ? 'OR' : 'AND';
          segments.push(expr.slice(start, i).trim());
          operators.push(op);
          i += opMatch[0].length;
          start = i;
          continue;
        }
      }
      i++;
    }

    segments.push(expr.slice(start).trim());
    return { segments, operators };
  }

  /** Same tokenization as ExpressionBuilder — longest rulename match for multi-word names. */
  private lexRuleExpression(expr: string, rulenames: string[]): string[] | null {
    const names = [...new Set(rulenames.map((s) => (s || '').trim()).filter(Boolean))].sort(
      (a, b) => b.length - a.length
    );
    const tokens: string[] = [];
    let i = 0;
    const n = expr.length;

    while (i < n) {
      while (i < n && /\s/.test(expr[i])) {
        i++;
      }
      if (i >= n) {
        break;
      }

      const c = expr[i];
      if (c === '(') {
        tokens.push('(');
        i++;
        continue;
      }
      if (c === ')') {
        tokens.push(')');
        i++;
        continue;
      }

      const rest = expr.slice(i);

      const kw = rest.match(/^(and|or)\b/i);
      if (kw) {
        tokens.push(kw[1].toLowerCase());
        i += kw[0].length;
        continue;
      }

      const num = rest.match(/^(\d+)/);
      if (num) {
        tokens.push(num[1]);
        i += num[1].length;
        continue;
      }

      let matched = false;
      for (const nm of names) {
        if (rest.length < nm.length) {
          continue;
        }
        if (rest.slice(0, nm.length).toLowerCase() === nm.toLowerCase()) {
          const boundary = rest[nm.length];
          if (boundary === undefined || /\s|[()]/.test(boundary)) {
            tokens.push(nm);
            i += nm.length;
            matched = true;
            break;
          }
        }
      }
      if (matched) {
        continue;
      }

      const id = rest.match(/^([\w.-]+)/);
      if (id) {
        tokens.push(id[1]);
        i += id[1].length;
        continue;
      }

      return null;
    }

    return tokens;
  }

  private parseSatExpressionAst(expr: string, rules: any[]): SatExprAstNode | null {
    const names = (rules || []).map((r: any) => r.rulename ?? '');
    const tokens = this.lexRuleExpression(expr, names);
    if (!tokens?.length) {
      return null;
    }
    this.satExprTok = tokens;
    this.satExprTi = 0;
    try {
      const node = this.parseSatOrLevel();
      if (this.satExprTi !== tokens.length) {
        return null;
      }
      return node;
    } catch {
      return null;
    }
  }

  private parseSatOrLevel(): SatExprAstNode {
    let left = this.parseSatAndLevel();
    while (
      this.satExprTi < this.satExprTok.length &&
      this.satExprTok[this.satExprTi].toLowerCase() === 'or'
    ) {
      this.satExprTi++;
      const right = this.parseSatAndLevel();
      left = { type: 'or', children: [left, right] };
    }
    return left;
  }

  private parseSatAndLevel(): SatExprAstNode {
    let left = this.parseSatPrimary();
    while (
      this.satExprTi < this.satExprTok.length &&
      this.satExprTok[this.satExprTi].toLowerCase() === 'and'
    ) {
      this.satExprTi++;
      const right = this.parseSatPrimary();
      left = { type: 'and', children: [left, right] };
    }
    return left;
  }

  private parseSatPrimary(): SatExprAstNode {
    const t = this.satExprTok[this.satExprTi++];
    if (t === '(') {
      const inner = this.parseSatOrLevel();
      const close = this.satExprTok[this.satExprTi++];
      if (close !== ')') {
        throw new Error('expected )');
      }
      return inner;
    }
    const lower = String(t).toLowerCase();
    if (lower === 'and' || lower === 'or') {
      throw new Error('unexpected operator');
    }
    if (/^\d+$/.test(t)) {
      return { type: 'num', value: t };
    }
    return { type: 'ref', value: String(t) };
  }

  private satAstToExpressionGroupObj(ast: SatExprAstNode, rules: any[]): any {
    const groups = this.satAstToFormGroups(ast, rules);
    if (!groups.length) {
      return { expressionGroupArray: [] };
    }
    return {
      expressionGroupArray: groups.map((g) => ({
        expressionArray: g.rows.map((r) => ({
          condition: r.condition,
          rule: r.rule,
        })),
        groupOperator: g.groupOperator,
      })),
    };
  }

  private satAstToFormGroups(
    node: SatExprAstNode,
    rules: any[]
  ): { rows: { condition: string; rule: string }[]; groupOperator: string }[] {
    const isLeaf = (n: SatExprAstNode) => n.type === 'num' || n.type === 'ref';

    if (isLeaf(node)) {
      return [{ rows: this.satAstToRows(node, rules), groupOperator: '' }];
    }

    if (node.type !== 'and' && node.type !== 'or') {
      return [{ rows: this.satAstToRows(node, rules), groupOperator: '' }];
    }
    const rootOpLabel = node.type === 'and' ? 'AND' : 'OR';
    const topTerms = this.collectSatSameOperatorTerms(node, node.type);

    if (topTerms.every((term) => isLeaf(term))) {
      return [{ rows: this.satAstToRows(node, rules), groupOperator: '' }];
    }

    const groups = topTerms
      .map((term) => ({
        rows: this.satAstToRows(term, rules),
        groupOperator: '',
      }))
      .filter((g) => g.rows.length > 0);

    for (let i = 0; i < groups.length - 1; i++) {
      groups[i].groupOperator = rootOpLabel;
    }
    return groups;
  }

  private collectSatSameOperatorTerms(
    node: SatExprAstNode,
    op: 'and' | 'or'
  ): SatExprAstNode[] {
    if (node.type !== op || !node.children || node.children.length < 2) {
      return [node];
    }
    const out: SatExprAstNode[] = [];
    for (const child of node.children) {
      out.push(...this.collectSatSameOperatorTerms(child, op));
    }
    return out;
  }

  private satAstToRows(node: SatExprAstNode, rules: any[]): { condition: string; rule: string }[] {
    if (node.type === 'num') {
      return [
        {
          condition: '',
          rule: this.resolveRuleTokenForSat(rules, node.value!, true),
        },
      ];
    }
    if (node.type === 'ref') {
      return [
        {
          condition: '',
          rule: this.resolveRuleTokenForSat(rules, node.value!, false),
        },
      ];
    }
    const kids = node.children || [];
    if (kids.length < 2) {
      return [];
    }
    const opLabel = node.type === 'and' ? 'AND' : 'OR';
    return this.mergeSatRows(
      opLabel,
      this.satAstToRows(kids[0], rules),
      this.satAstToRows(kids[1], rules)
    );
  }

  private mergeSatRows(
    operator: string,
    left: { condition: string; rule: string }[],
    right: { condition: string; rule: string }[]
  ): { condition: string; rule: string }[] {
    if (!left.length) {
      return right;
    }
    if (!right.length) {
      return left;
    }
    return [
      ...left,
      { condition: operator, rule: right[0].rule },
      ...right.slice(1),
    ];
  }

  private resolveRuleTokenForSat(rules: any[], token: string, isNumeric: boolean): string {
    if (isNumeric) {
      const n = Number(token);
      const match = rules.find((x: any) => Number(x?.rulenumber ?? 0) === n);
      if (match) {
        return ((match.rulename ?? '') as string).toString().trim() || `Rule${token}`;
      }
      if (rules.length && n >= 1 && n <= rules.length) {
        return ((rules[n - 1].rulename ?? '') as string).toString().trim() || `Rule${token}`;
      }
      return `Rule${token}`;
    }
    const raw = (token ?? '').toString().trim();
    const byName = rules.find(
      (r: any) => (r.rulename ?? '').toString().trim().toLowerCase() === raw.toLowerCase()
    );
    return ((byName?.rulename ?? '') as string).toString().trim() || raw;
  }

  /** Previous single-group split — kept when AST parsing cannot handle the string. */
  private parseRuleSatisfactionToExpressionLegacyFlatten(
    ruleSatisfaction: string,
    rules: any[]
  ): any {
    const inner = ruleSatisfaction.replace(/^\(|\)$/g, '').trim();
    const tokens = inner.split(/\s+(AND|OR)\s+/i);
    const expressionArray: { condition: string; rule: string }[] = [];
    let cond = '';
    for (const t of tokens) {
      if (t.toUpperCase() === 'AND' || t.toUpperCase() === 'OR') {
        cond = t.toUpperCase();
      } else {
        const rawToken = (t || '').toString().trim();
        const numericMatch = rawToken.match(/\d+/);
        const num = numericMatch ? parseInt(numericMatch[0], 10) : NaN;
        const ruleByNumber = !isNaN(num)
          ? rules.find((x: any) => Number(x?.rulenumber ?? 0) === num)
          : null;
        const resolvedRule =
          (ruleByNumber?.rulename || '').toString().trim() ||
          (!isNaN(num) ? `Rule${num}` : rawToken || 'Rule1');
        expressionArray.push({ condition: cond, rule: resolvedRule });
        cond = '';
      }
    }
    return {
      expressionGroupArray: [{ expressionArray, groupOperator: '' }],
    };
  }

  private buildFinalExpressionFromBuilderData(builderData: any): string {
    return this.buildExpressionFromBuilderData(builderData, (rawRule) =>
      rawRule.replace(/^Rule\s*/i, '')
    );
  }

  /** Persisted API format — rule numbers instead of display labels (Rule Config algorithm). */
  convertNamedExpressionToNumbers(expr: string, rules: any[]): string {
    const normalizedRules = (rules || []).map((r: any, idx: number) => ({
      rulenumber: r.rulenumber ?? idx + 1,
      rulename: (r.rulename || '').trim() || `Rule${r.rulenumber ?? idx + 1}`,
    }));
    return convertNamedRuleExpressionToNumbers(expr, normalizedRules);
  }

  private buildExpressionFromBuilderData(
    builderData: any,
    mapRuleToken: (rawRule: string) => string
  ): string {
    const groups = builderData?.expressionGroupArray;
    if (!Array.isArray(groups) || groups.length === 0) {
      return '';
    }

    const buildRange = (start: number, end: number): string => {
      const parts: string[] = [];
      for (let index = start; index <= end; index++) {
        const group = groups[index];
        const expressions = Array.isArray(group?.expressionArray) ? group.expressionArray : [];
        const innerExpression = expressions
          .map((exp: any, rowIdx: number) => {
            const rawRule = `${exp?.rule ?? ''}`.trim();
            const ruleToken = mapRuleToken(rawRule);
            return `${rowIdx > 0 && exp?.condition ? exp.condition + ' ' : ''}${ruleToken}`.trim();
          })
          .filter((p: string) => !!p)
          .join(' ');
        const trimmedInner = innerExpression.trim();
        if (!trimmedInner) return '';
        if (!trimmedInner) continue;
        parts.push(`(${trimmedInner})`);
        if (index < end && group?.groupOperator) {
          parts.push(group.groupOperator);
        }
      }
      return parts.join(' ').trim();
    };

    const groupedPrefixEndIndex =
      typeof builderData?.groupedPrefixEndIndex === 'number'
        ? builderData.groupedPrefixEndIndex
        : null;
    if (
      groupedPrefixEndIndex !== null &&
      groupedPrefixEndIndex >= 0 &&
      groupedPrefixEndIndex < groups.length - 1
    ) {
      const prefix = buildRange(0, groupedPrefixEndIndex);
      const suffix = buildRange(groupedPrefixEndIndex + 1, groups.length - 1);
      const connector = (groups[groupedPrefixEndIndex]?.groupOperator || '').toString().trim();
      return connector ? `(${prefix}) ${connector} ${suffix}` : `(${prefix}) ${suffix}`;
    }
    return buildRange(0, groups.length - 1);
  }

  private inferGroupedPrefixEndIndexFromExpression(expr: string): number | null {
    const normalized = (expr || '').toString().trim();
    if (!normalized) {
      return null;
    }
    const top = this.splitTopLevelSegments(normalized);
    if (top.segments.length < 2) {
      return null;
    }
    const first = (top.segments[0] || '').trim();
    const unwrapped = this.unwrapOuterParens(first);
    if (unwrapped === first) {
      return null;
    }
    const firstTop = this.splitTopLevelSegments(unwrapped);
    return firstTop.segments.length > 1 ? 0 : null;
  }

  private expandGroupedPrefixForVisualBuilder(expr: string, rules: any[]): any | null {
    const normalized = (expr || '')
      .toString()
      .replace(/\bAND\b/gi, 'and')
      .replace(/\bOR\b/gi, 'or')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) {
      return null;
    }

    const outer = this.splitTopLevelSegments(normalized);
    if (outer.segments.length < 2) {
      return null;
    }

    const firstInner = this.unwrapOuterParens((outer.segments[0] || '').trim());
    const firstTop = this.splitTopLevelSegments(firstInner);
    if (firstTop.segments.length < 2) {
      return null;
    }
    const hasAnyWrappedSubgroup = firstTop.segments.some((seg) => {
      const s = (seg || '').trim();
      return s.startsWith('(') && s.endsWith(')');
    });
    if (!hasAnyWrappedSubgroup) {
      return null;
    }

    const expressionGroupArray: any[] = [];
    for (let i = 0; i < firstTop.segments.length; i++) {
      const seg = (firstTop.segments[i] || '').trim();
      if (!seg) continue;
      const ast = this.parseSatExpressionAst(seg, rules);
      if (!ast) return null;
      const rows = this.satAstToRows(ast, rules).map((r) => ({
        condition: r.condition,
        rule: r.rule,
      }));
      if (!rows.length) continue;
      expressionGroupArray.push({
        expressionArray: rows,
        groupOperator:
          i < firstTop.operators.length
            ? firstTop.operators[i]
            : (outer.operators[0] || ''),
      });
    }

    for (let oi = 1; oi < outer.segments.length; oi++) {
      const seg = (outer.segments[oi] || '').trim();
      if (!seg) continue;
      const ast = this.parseSatExpressionAst(seg, rules);
      if (!ast) return null;
      const rows = this.satAstToRows(ast, rules).map((r) => ({
        condition: r.condition,
        rule: r.rule,
      }));
      if (!rows.length) continue;
      expressionGroupArray.push({
        expressionArray: rows,
        groupOperator: oi < outer.operators.length ? outer.operators[oi] : '',
      });
    }

    if (expressionGroupArray.length < 2) {
      return null;
    }
    return {
      expressionGroupArray,
      groupedPrefixEndIndex: firstTop.segments.length - 1,
    };
  }

  private unwrapOuterParens(text: string): string {
    let s = (text || '').trim();
    if (!s.startsWith('(') || !s.endsWith(')')) {
      return s;
    }
    let depth = 0;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === '(') depth++;
      if (ch === ')') depth--;
      if (depth === 0 && i < s.length - 1) {
        return s;
      }
    }
    return s.slice(1, -1).trim();
  }

  buildFinalFromBuilderData(builderData: unknown): string {
    return this.buildFinalExpressionFromBuilderData(builderData);
  }

  /**
   * UI / Build Expression text from saved ruleSatisfaction.
   *
   * Load with rulenames: API "(2 AND 6)" → "(test AND testobr)"
   * Legacy (no rulename): API "(1 AND 2)" → "(Rule1 AND Rule2)"
   */
  resolveFinalExpressionForBlock(
    block: { rules: unknown[]; ruleSatisfaction: string }
  ): string {
    const sat = (block.ruleSatisfaction || '').trim();
    const rawRules = (block.rules || []).map((r: any, idx: number) => ({
      rulenumber: r.rulenumber ?? idx + 1,
      rulename: (r.rulename ?? '').toString().trim(),
    }));

    const rulesForBuilder = rawRules.map((r) => ({
      rulenumber: r.rulenumber,
      rulename: r.rulename || defaultRuleDisplayLabel(Number(r.rulenumber)),
      field: '',
      value: '',
      operator: '',
      ruletype: '',
      param: '',
    }));

    if (!sat) {
      if (!rawRules.length) {
        return '';
      }
      return this.buildFinalExpressionFromBuilderData(
        this.buildDefaultExpressionFromRules(rulesForBuilder)
      );
    }

    const fromNumbers = convertNumberedRuleExpressionToNames(sat, rawRules);
    if (fromNumbers) {
      return fromNumbers;
    }

    const tokens = extractExpressionRuleTokens(sat);
    if (tokens.length > 0 && tokens.some((t) => !/^\d+$/.test(t))) {
      return sat;
    }

    const expressionGroupObj = this.parseRuleSatisfactionToExpressionGroups(
      sat,
      rulesForBuilder
    );
    const built = this.buildFinalExpressionFromBuilderData(expressionGroupObj);
    return built || sat;
  }

  /**
   * Named expression for all rules AND-chained — single rule becomes "(rulename)".
   * @example one rule "test" → "(test)"
   */
  buildDefaultRuleSatisfaction(
    rules: { rulenumber?: number; rulename?: string }[]
  ): string {
    if (!rules?.length) {
      return '';
    }
    const normalized = rules.map((r, idx) => ({
      rulenumber: r.rulenumber ?? idx + 1,
      rulename:
        (r.rulename || '').trim() ||
        defaultRuleDisplayLabel(Number(r.rulenumber ?? idx + 1)),
    }));
    return this.buildFinalExpressionFromBuilderData(
      this.buildDefaultExpressionFromRules(normalized)
    );
  }

  /** When the first rule is added, seed ruleSatisfaction if still empty. */
  seedRuleSatisfactionIfFirstRule(block: {
    rules: { rulenumber?: number; rulename?: string }[];
    ruleSatisfaction: string;
  }): void {
    if (block.rules.length !== 1) {
      return;
    }
    const sat = (block.ruleSatisfaction ?? '').toString().trim();
    if (sat && sat !== '1') {
      return;
    }
    const seeded = this.buildDefaultRuleSatisfaction(block.rules);
    if (seeded) {
      block.ruleSatisfaction = seeded;
    }
  }
}

export const routingExpressionPrep = new RoutingExpressionPrep();
